import { useQuery } from "@tanstack/react-query";
import type { DashboardMode } from "@nexus/contracts";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { writeActivePresetId } from "@/features/dashboard/lib/active-preset-storage";
import { buildCurrentPresetPayload } from "@/features/overview/lib/filter-preset-utils";
import {
  overviewFilterPresetsQueryOptions,
  useCreateOverviewFilterPreset,
} from "@/lib/queries/filter-presets.queries";
import { SaveFilterPresetControl } from "./SaveFilterPresetControl";

interface OverviewSaveFilterPresetProps {
  mode: DashboardMode;
  canSavePreset?: boolean;
  onPresetCreated?: (presetId: string) => void;
}

export function OverviewSaveFilterPreset({
  mode,
  canSavePreset = true,
  onPresetCreated,
}: OverviewSaveFilterPresetProps) {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const permissions = usePermissions();
  const canListPresets = useIamPermission(permissions.OVERVIEW_VIEW_PAGE);
  const canShareWithCompany = useIamPermission(permissions.OVERVIEW_SHARE_FILTER_PRESET);
  const createMutation = useCreateOverviewFilterPreset(mode);
  const { data: presets = [] } = useQuery({
    ...overviewFilterPresetsQueryOptions(mode, activeTenantId),
    enabled: canListPresets && Boolean(activeTenantId),
  });

  if (!canListPresets) return null;

  return (
    <SaveFilterPresetControl
      disabled={!canSavePreset}
      presets={presets}
      isPending={createMutation.isPending}
      i18nPrefix="overview.filterPresets"
      canShareWithCompany={canShareWithCompany}
      onSave={async (name, shareWithCompany) => {
        const payload = buildCurrentPresetPayload(mode);
        const created = await createMutation.mutateAsync({
          name,
          shareWithCompany,
          ...payload,
        });
        writeActivePresetId("overview", mode, created.id);
        onPresetCreated?.(created.id);
      }}
    />
  );
}
